import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, Save, Image as ImageIcon, AlertCircle, Bold, Heading, Info } from 'lucide-react';
import { diagramService } from '@/api/services';
import { DiagramData } from '@/types';
import { useToastContext } from '@/contexts/ToastContext';

interface DiagramEditorProps {
  weeklyReportId: number;
  isEditing?: boolean;
  onDiagramChange?: (data: DiagramData | null) => void;
}

/**
 * Renders the diagram description markup the same way the exported report does:
 * lines starting with "# " become headings and "**text**" becomes bold.
 */
function renderRichPreview(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const lines = (text || '').split('\n');
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) {
      nodes.push(<div key={idx} className="py-1" />);
      return;
    }
    const heading = trimmed.match(/^# (.*)$/);
    const content = heading ? heading[1] : trimmed;
    const parts = content.split(/(\*\*[^*]+\*\*)/g);
    const segments = parts.map((part, i) => {
      if (!part) return null;
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
    nodes.push(
      heading ? (
        <p key={idx} className="font-bold text-lg text-gray-900 mb-2">
          {segments}
        </p>
      ) : (
        <p key={idx} className="text-justify text-gray-700 mb-1">
          {segments}
        </p>
      )
    );
  });
  return nodes;
}

export const DiagramEditor: React.FC<DiagramEditorProps> = ({ weeklyReportId, isEditing = false, onDiagramChange }) => {
  const { showSuccess, showError } = useToastContext();
  const [diagramData, setDiagramData] = useState<DiagramData | null>(null);
  const [text, setText] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showDescriptionInfo, setShowDescriptionInfo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  // Load existing diagram data
  useEffect(() => {
    loadDiagramData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyReportId]);

  // Notify the parent (header "Have Diagram" tag) whenever the diagram changes.
  useEffect(() => {
    onDiagramChange?.(diagramData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramData]);

  // Close the full-size image viewer with the Escape key.
  useEffect(() => {
    if (!showImageViewer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowImageViewer(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showImageViewer]);

  const loadDiagramData = async () => {
    try {
      setIsLoading(true);
      const response = await diagramService.getDiagram(weeklyReportId);
      if (response.success && response.data) {
        setDiagramData(response.data);
        setText(response.data.text || '');
        if (response.data.image?.url) {
          setImagePreview(response.data.image.url);
        }
      }
    } catch (err) {
      console.error('Failed to load diagram data:', err);
      setError('Failed to load diagram data');
      showError('Failed to load diagram data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveText = async () => {
    try {
      setIsLoading(true);
      const method = diagramData?.exists ? 'updateDiagramText' : 'createDiagram';
      const response = await diagramService[method](weeklyReportId, text);

      if (response.success) {
        setDiagramData(response.data);
        setError(null);
        showSuccess(diagramData?.exists ? 'Diagram description saved successfully!' : 'Diagram created successfully!');
      }
    } catch (err) {
      console.error('Failed to save diagram text:', err);
      setError('Failed to save diagram text');
      showError('Failed to save diagram description. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyBold = () => {
    const ta = descriptionRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const lineStartsAt = text.lastIndexOf('\n', start - 1) + 1;
    let from = start;
    let to = end;
    if (start === end) {
      from = lineStartsAt;
      const nl = text.indexOf('\n', start);
      to = nl === -1 ? text.length : nl;
    }
    const selection = text.slice(from, to);
    const next = text.slice(0, from) + '**' + selection + '**' + text.slice(to);
    setText(next);
    const newTo = to + 4;
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(from, newTo);
    }, 0);
  };

  const applyHeading = () => {
    const ta = descriptionRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const from = text.lastIndexOf('\n', start - 1) + 1;
    const nl = text.indexOf('\n', start);
    const to = nl === -1 ? text.length : nl;
    const line = text.slice(from, to);
    let next;
    if (/^# /.test(line)) {
      next = text.slice(0, from) + line.replace(/^# /, '') + text.slice(to);
    } else {
      next = text.slice(0, from) + '# ' + line + text.slice(to);
    }
    setText(next);
    setTimeout(() => {
      ta.focus();
    }, 0);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      showError('Please select a valid image file (JPEG, PNG or WEBP).');
      return;
    }

    // Validate file size (max 10MB as per backend)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      showError('File size must be less than 10MB.');
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleUploadImage = async () => {
    if (!imageFile) return;

    try {
      setIsUploading(true);
      const response = await diagramService.uploadDiagramImage(weeklyReportId, imageFile);

      if (response.success) {
        setDiagramData(response.data.document);
        setImageFile(null);
        setImagePreview(response.data.document?.image?.url || null);
        setError(null);
        showSuccess('Diagram image uploaded successfully!');
      }
    } catch (err) {
      console.error('Failed to upload diagram image:', err);
      setError('Failed to upload diagram image');
      showError('Failed to upload diagram image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteImage = async () => {
    try {
      setIsLoading(true);
      await diagramService.deleteDiagramImage(weeklyReportId);

      setDiagramData(prev => prev ? { ...prev, image: null, images: [] } : null);
      setImagePreview(null);
      setImageFile(null);
      setError(null);
      showSuccess('Diagram image deleted successfully.');
    } catch (err) {
      console.error('Failed to delete diagram image:', err);
      setError('Failed to delete diagram image');
      showError('Failed to delete diagram image. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePreview = () => {
    setImageFile(null);
    setImagePreview(diagramData?.image?.url || null);
  };

  if (isLoading && !diagramData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Diagram Image Section with Template */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4 gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Diagram Image</h3>
          {isEditing && (
            <div className="flex items-center gap-2">
              {imageFile ? (
                <>
                  <button
                    onClick={handleUploadImage}
                    disabled={isUploading}
                    className="btn-primary flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap"
                  >
                    <Save className="w-4 h-4" />
                    {isUploading ? 'Uploading...' : 'Save Image'}
                  </button>
                  <button
                    onClick={handleRemovePreview}
                    disabled={isUploading}
                    className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="btn-primary flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap"
                  >
                    <Upload className="w-4 h-4" />
                    {imagePreview ? 'Replace Image' : 'Upload Image'}
                  </button>
                  {imagePreview && (
                    <button
                      onClick={handleDeleteImage}
                      disabled={isLoading}
                      className="text-red-600 hover:text-red-800 flex items-center gap-1.5 text-sm whitespace-nowrap"
                    >
                      <X className="w-4 h-4" />
                      Delete Image
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Diagram Template Area */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-white">
          {isEditing && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          )}

          {/* Template Header */}
          <div className="text-center mb-4 pb-2 border-b border-gray-300">
            <h4 className="text-xl font-bold text-gray-900">Details of main job</h4>
          </div>

          {/* Main Content Area */}
          <div className="min-h-[300px] flex flex-col items-center justify-center relative">
            {imagePreview ? (
              // Image Preview (click to view full size)
              <div className="relative w-full">
                <img
                  src={imagePreview}
                  alt="Diagram preview"
                  onClick={() => setShowImageViewer(true)}
                  className="w-full h-auto max-h-[400px] object-contain mx-auto cursor-zoom-in"
                  role="button"
                  aria-label="Open diagram image full size"
                  title="Click to view full size"
                />
                <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[11px] px-2 py-1 rounded pointer-events-none">
                  <ImageIcon className="w-3 h-3 inline mr-1" />
                  Click to enlarge
                </span>
                {imageFile && isEditing && (
                  <span className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                    Image selected - click "Save Image" to apply
                  </span>
                )}
              </div>
            ) : (
              // Upload Area with Central Button
              <div className="text-center">
                {isEditing ? (
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`border-2 border-dashed rounded-lg p-8 transition-colors ${
                      isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 mb-4 transition-colors"
                      >
                        <Upload className="w-8 h-8" />
                      </button>
                      <p className="text-gray-600 mb-2">
                        Drag and drop an image here, or click to browse
                      </p>
                      <p className="text-gray-400 text-sm">
                        Supported formats: JPEG, PNG, WEBP (max 10MB)
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400">
                    <ImageIcon className="w-16 h-16 mx-auto mb-2" />
                    <p>No diagram image uploaded yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}
      </div>

      {/* Diagram Text Section */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">Diagram Description</h3>
            <span className="inline-flex items-center text-[11px] lg:text-xs font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
              Optional
            </span>
            <button
              type="button"
              onClick={() => setShowDescriptionInfo(v => !v)}
              aria-expanded={showDescriptionInfo}
              aria-label="About the diagram description"
              title="Why add a description?"
              className="text-gray-400 hover:text-gray-600 p-1 -m-1"
            >
              <Info className="w-4 h-4" />
            </button>
          </div>
          {isEditing && (
            <button
              onClick={handleSaveText}
              disabled={isLoading}
              className="btn-primary flex items-center gap-2 px-3 py-1.5 text-sm"
            >
              <Save className="w-4 h-4" />
              {isLoading ? 'Saving...' : 'Save Text'}
            </button>
          )}
        </div>

        {showDescriptionInfo && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <p className="leading-relaxed">
              The description is <strong>optional</strong> — it is not required to add a diagram.
              However, adding a well-written description is an added advantage that demonstrates
              professionalism. The description will be included on the diagram page of the weekly
              report, right before the diagram.
            </p>
          </div>
        )}

        {isEditing ? (
          <>
            {/* Formatting toolbar */}
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={applyBold}
                title="Bold selected text or the current line (**bold**)"
                className="format-btn flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Bold className="w-4 h-4" />
                <span className="text-xs font-medium">B</span>
              </button>
              <button
                type="button"
                onClick={applyHeading}
                title="Toggle heading on the current line (# heading)"
                className="format-btn flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Heading className="w-4 h-4" />
                <span className="text-xs font-medium">H</span>
              </button>
              <span className="text-xs text-gray-400 ml-1">
                Style the description exactly as it will appear in the report (Bold + Heading).
              </span>
            </div>

            <textarea
              ref={descriptionRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add diagram description... Use **bold** for bold text and start a line with # for a heading."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none font-mono text-sm"
              rows={6}
            />

            {/* Live preview matching the exported report */}
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Preview</p>
              {text ? (
                <div>{renderRichPreview(text)}</div>
              ) : (
                <p className="text-gray-400 text-sm">No description yet.</p>
              )}
            </div>
          </>
        ) : (
          <div className="text-gray-700 p-3 bg-gray-50 rounded-lg min-h-[100px]">
            {text ? renderRichPreview(text) : <span className="text-gray-400">No diagram description added yet</span>}
          </div>
        )}
      </div>

      {/* Full-size image viewer */}
      {showImageViewer && imagePreview && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4"
          onClick={() => setShowImageViewer(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Diagram image enlarged view"
        >
          <div className="relative max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowImageViewer(false)}
              className="absolute -top-3 -right-3 bg-white text-gray-800 rounded-full p-1.5 shadow-lg hover:bg-gray-100"
              aria-label="Close enlarged image"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={imagePreview}
              alt="Diagram image enlarged"
              className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};